import React, { useMemo, useCallback } from 'react';
import { isEqual } from 'lodash';
import { Form, InputNumber, Input, Collapse, Card, Tag } from 'antd';
import { FormInstance } from 'antd/lib/form';

import DataGrid from 'components/DataGrid/DataGrid';
import { DataGridColDef } from 'components/DataGrid/DataGrid';
import DF from 'DisplayFramework';
import { useFormList } from 'hooks/useFormList';
import { useModalState } from 'hooks/useModalState';
import { indexBy } from 'utils/utils2';
import AssetListModal from '../../Asset/AssetListModal';
import { getAssetsByID, AssetListSearchResponse } from 'services/assets';
import { PurOrderLine, PurOrderLineResponse } from 'services/purchaseOrder';
import { toCurrency } from 'utils/utils';
import Title from 'components/Title';
import { CheckCircleOutlined } from '@ant-design/icons';
import InputCurrency from 'components/InputCurrency';
import AssetDetails from 'pages/Asset/AssetDetail';
import ModalControllerNV from 'components/ModalControllerNV';
import _ from 'lodash';
const NEW_LINE_ID = -1;

type Values = {
  lines: PurOrderLine[];
};

type PurOrderLinesTableProps = {
  form: FormInstance;
  refetch: () => void;
};

const priceAfterDiscount = (initialPrice: number, discount = 0, total = 1) =>
  (total * (initialPrice * (100 - discount))) / 100 || initialPrice;

const PurOrderLinesTable = (props: PurOrderLinesTableProps) => {
  const { form } = props;
  const { close, visible } = useModalState();

  const fieldName = 'purOrderLines';

  const { add, remove } = useFormList(form, fieldName, {
    idKey: 'polid',
    addAsFirst: true,
  });

  const getName = useCallback(
    (name: string) => (record: PurOrderLine) => {
      const filters = form.getFieldValue(fieldName);
      const index = indexBy(filters, 'polid')[record.polid];
      return [fieldName, index, name];
    },
    [form]
  );
  type PurOrderLineWithTaxRate = PurOrderLine & {
    taxrate: number;
  };

  type PurOrderLineResponseWithTaxRate = PurOrderLineResponse & {
    taxrate: number;
  };

  const onAssetSelect = (selectedAsset: AssetListSearchResponse) => {
    getAssetsByID({ assetid: selectedAsset.assetid }).then((response) => {
      const { data: asset } = response;
      const row: PurOrderLineResponseWithTaxRate = {
        poid: NEW_LINE_ID,
        objectlabel: asset.assetcategorylabel || asset.assetcategory,
        objectreference: '',
        poltypelabel: 'Asset',
        statuslabel: asset.statuslabel,
        discount: 0,
        grossamount: asset.assetgrossamount,
        netafterdiscount: asset.assetnetamount,
        objectid: NEW_LINE_ID,
        poldescription: '',
        polid: Math.round(_.random(10000) * 100),
        polreference: String(asset.assetid),
        poltype: 'ASSET',
        qty: 1,
        status: 'INIT',
        statusdate: '',
        taxamount: asset.assettaxamount,
        taxcode: asset.taxcode,
        taxrate: asset.taxrate,
        totalnetafterdiscount: NEW_LINE_ID,
        unitprice: asset.assetnetamount,
      };
      add(row);
      close();
    });
  };

  const calculatePOLines = (lines: PurOrderLineWithTaxRate[]): void => {
    let pogrossamount = 0;
    let potaxamount = 0;
    let ponetamount = 0;
    const calculatedLines = lines.map((line) => {
      const netafterdiscount = priceAfterDiscount(
        line.unitprice,
        line.discount
      );
      const totalnetafterdiscount = netafterdiscount * line.qty;
      let taxrate = line.taxrate;
      if (taxrate === undefined) {
        taxrate = (line.taxamount / totalnetafterdiscount) * 100;
      }
      const taxamount = (taxrate * totalnetafterdiscount) / 100;
      const grossamount = totalnetafterdiscount + taxamount;
      pogrossamount += grossamount;
      potaxamount += taxamount;
      ponetamount += totalnetafterdiscount;
      return {
        ...line,
        netafterdiscount,
        totalnetafterdiscount,
        taxamount,
        grossamount,
      };
    });
    form.setFieldsValue({
      pogrossamount,
      potaxamount,
      ponetamount,
      [fieldName]: calculatedLines,
    });
  };

  const columns = useMemo<DataGridColDef<PurOrderLineWithTaxRate>[]>(
    () => [
      {
        dataIndex: 'polreference',
        render: (value, record) => (
          <ModalControllerNV
            content={({ setVisible, visible }) => (
              <a onClick={() => setVisible(true)}>{record.polreference}</a>
            )}
            modal={({ setVisible, visible }) => (
              <AssetDetails
                assetid={record.objectid}
                onCancel={() => setVisible(false)}
                showAsModal
                visible={visible}
                phaseprop="PROPOSAL"
                iscreate={false}
              />
            )}
          ></ModalControllerNV>
        ),
        fixed: 'left',
        width: '20%',
      },
      // {
      //   dataIndex: 'poltypelabel',
      //   render: createEditableCell({
      //     getName: getName('poltype'),
      //   }),
      //   fixed: 'left',
      //   width:'20%'

      // },
      {
        comcode: 'objectlabel',
        dataIndex: 'assetlabel',
        render: (value, record) => (
          <Form.Item noStyle name={getName('assetlabel')(record)}>
            <Input />
          </Form.Item>
        ),

        width: '40%',
      },
      {
        dataIndex: 'unitprice',
        render: (value, record) => (
          <Form.Item noStyle name={getName('unitprice')(record)}>
            <InputCurrency
              currcode={form.getFieldValue('currcode')}
              style={{ width: '90%' }}
              disabled
            />
          </Form.Item>
        ),
        width: '25%',
      },
      {
        dataIndex: 'discount',
        render: (value, record) => (
          <Form.Item noStyle name={getName('discount')(record)}>
            <InputNumber
              defaultValue={0}
              min={0}
              max={99}
              disabled
              formatter={(value: any) => `${value}%`}
              parser={(value: any) => value.replace('%', '')}
            />
          </Form.Item>
        ),

        width: '20%',
      },
      {
        dataIndex: 'netafterdiscount',
        render: (value) =>
          toCurrency(Number(value), form.getFieldValue('currcode')),
        width: '20%',
      },
      {
        dataIndex: 'qty',
        render: (value, record) => (
          <Form.Item noStyle name={getName('qty')(record)}>
            <InputNumber disabled defaultValue={1} min={1} />
          </Form.Item>
        ),

        width: '20%',
      },
      {
        dataIndex: 'totalnetafterdiscount',
        render: (value) =>
          toCurrency(Number(value).toFixed(2), form.getFieldValue('currcode')),
        width: '20%',
      },
      {
        dataIndex: 'taxcode',
        width: '15%',
      },
      {
        dataIndex: 'taxamount',
        render: (value) =>
          toCurrency(Number(value).toFixed(2), form.getFieldValue('currcode')),
        width: '20%',
      },
      {
        dataIndex: 'grossamount',
        render: (value) =>
          toCurrency(Number(value).toFixed(2), form.getFieldValue('currcode')),
        fixed: 'right',
        width: '20%',
      },
      {
        comcode: 'statuslabel',
        dataIndex: 'statuslabel',
        render: (_, record) => {
          return (
            <div>
              <Tag
                icon={record.status === 'VALID' && <CheckCircleOutlined />}
                color={record.status === 'VALID' ? 'success' : 'default'}
              >
                {record.statuslabel}
              </Tag>
            </div>
          );
        },
        fixed: 'right',
        width: '15%',
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [getName, remove]
  );

  return (
    <DF.Provider pagcode="TODO">
      <Form.Item
        noStyle
        shouldUpdate={(prevValue: Values, nextValue: Values) => {
          return !isEqual(prevValue, nextValue);
        }}
      >
        {({ getFieldValue }) => {
          const lines: PurOrderLineWithTaxRate[] = getFieldValue(fieldName);
          calculatePOLines(lines);
          return (
            <div>
              <Collapse bordered={false} expandIconPosition="left">
                <Card style={{ backgroundColor: '#f7f7f7', padding: 12 }}>
                  <Title comcode="details" level={5}></Title>
                  <div style={{ padding: 5, background: 'white' }}>
                    <DataGrid
                      scroll={{ x: 1500, y: 600 }}
                      rowKey="polid"
                      dataSource={lines}
                      columns={columns}
                    />
                  </div>
                </Card>
              </Collapse>
            </div>
          );
        }}
      </Form.Item>
      <AssetListModal
        visible={visible}
        onCancel={close}
        rowSelection={{
          type: 'checkbox',
          hideSelectAll: true,
          onSelect: onAssetSelect,
        }}
      />
    </DF.Provider>
  );
};

export default PurOrderLinesTable;
